import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#CBCECE] to-[#77D8EE]">
      <Navbar />

      <div className="pt-32 pb-16 px-4 max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4 text-center">
            Privacy Policy
          </h1>
          <p className="text-center text-gray-600 mb-8">
            LAST REVISED: August 11th, 2025
          </p>

          <div className="text-gray-700 space-y-6">
            <section>
              <p className="text-gray-600 leading-relaxed mb-6">
                <strong>
                  Plutonic, Inc. ("Plutonic," "we," "our," or "us")
                </strong>{" "}
                values the privacy of those who visit our website, use our
                online platform, or receive our in-person therapy services
                (collectively, the "Services"). We take your privacy seriously
                and are committed to protecting the personal and health
                information we collect from and about you.
              </p>
              <p className="text-gray-600 leading-relaxed mb-6">
                This Privacy Policy describes:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4 mb-6">
                <li>What personal information we collect</li>
                <li>How and why we use your information</li>
                <li>How we protect your information</li>
                <li>To whom your information may be disclosed</li>
                <li>Your rights regarding your information</li>
                <li>How to provide or withdraw consent</li>
              </ul>
              <p className="text-gray-600 leading-relaxed mb-8">
                <strong>
                  By using our Services, whether online or in person, you
                  consent to the practices described in this Privacy Policy. If
                  you do not agree, you must discontinue use of the Services.
                </strong>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                1. Scope of This Privacy Policy
              </h2>
              <p className="text-gray-600 leading-relaxed">
                This Privacy Policy applies to all Services offered by Plutonic,
                whether delivered through our website at
                https://www.plutonic.co, our virtual therapy platform, or in
                person at one of our physical office locations. All personal and
                health information collected in connection with these services
                is subject to the protections described in this Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                2. Types of Information We Collect
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We collect two main categories of information:
              </p>
              <h3 className="text-xl font-medium text-gray-700 mb-3">
                a. Personal Information
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Information that identifies you or could reasonably be used to
                identify you, such as your name, address, email address, phone
                number, and payment details.
              </p>
              <h3 className="text-xl font-medium text-gray-700 mb-3">
                b. Protected Health Information (PHI)
              </h3>
              <p className="text-gray-600 leading-relaxed">
                If you engage in therapy services with Plutonic, whether in
                person or online, we may collect and store Protected Health
                Information ("PHI") as defined by the Health Insurance
                Portability and Accountability Act of 1996 ("HIPAA"). This
                includes information you provide during intake, assessments,
                therapy sessions, and follow-up communications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                3. How We Collect Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may collect information in the following ways:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  Directly from you: When you create an account, schedule
                  appointments, complete intake forms, or participate in therapy
                  sessions.
                </li>
                <li>
                  Automatically: Through cookies, analytics tools, and similar
                  technologies when you visit our website.
                </li>
                <li>
                  From third parties: Such as your insurance provider (if
                  applicable), referral sources, or integrated service providers
                  (e.g., HIPAA-compliant EHR systems).
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                If you are under 13 years old, we will only collect your
                information with verifiable parental consent as required by the
                Children's Online Privacy Protection Act ("COPPA").
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                4. How We Use Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use your personal and health information to:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Provide, manage, and improve our therapy services</li>
                <li>Communicate with you about appointments and services</li>
                <li>Process payments and insurance claims (if applicable)</li>
                <li>
                  Comply with legal, regulatory, and licensing requirements
                </li>
                <li>
                  Ensure the safety, security, and quality of our Services
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                We will not sell or rent your personal information.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                5. How We Protect Your Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We use industry-standard safeguards to protect your personal and
                health information, including:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  Secure, HIPAA-compliant electronic health record (EHR) systems
                  for all client records
                </li>
                <li>Encryption of data in transit and at rest</li>
                <li>
                  Access controls limiting PHI access to authorized personnel
                  only
                </li>
                <li>
                  Confidentiality agreements for all employees, contractors, and
                  providers
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                6. Sharing and Disclosure of Information
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                We may share your information in the following situations:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>With your consent:</strong> For example, sharing
                  records with another provider at your request.
                </li>
                <li>
                  <strong>With service providers:</strong> Third-party vendors
                  who perform services for us (e.g., telehealth platform,
                  billing services, EHR) under HIPAA-compliant agreements.
                </li>
                <li>
                  <strong>For legal compliance:</strong> When required by law,
                  court order, or regulatory authority.
                </li>
                <li>
                  <strong>For safety reasons:</strong> If necessary to prevent
                  or lessen a serious threat to health or safety.
                </li>
                <li>
                  <strong>In a business transfer:</strong> If Plutonic is
                  involved in a merger, acquisition, or sale of assets.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                7. Special Provisions for Clients Under 18
              </h2>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong>Under 18:</strong> Parental or guardian consent is
                  required for therapy services.
                </li>
                <li>
                  <strong>Under 13:</strong> We comply with COPPA and require
                  verifiable parental consent before collecting, storing, or
                  using any personal information.
                </li>
                <li>
                  We may require the consenting parent or guardian to
                  participate in the intake process and provide proof of
                  identity.
                </li>
                <li>If consent is withdrawn, services will be discontinued.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                8. Breach Notification
              </h2>
              <p className="text-gray-600 leading-relaxed">
                In the event of a breach of unsecured PHI, Plutonic will notify
                affected individuals without unreasonable delay, as required by
                HIPAA and applicable state laws. We will also notify the U.S.
                Department of Health and Human Services (HHS) and, if
                applicable, state regulators.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                9. Your Rights
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                You have the right to:
              </p>
              <ul className="text-gray-600 list-disc list-inside space-y-2 ml-4 mb-4">
                <li>
                  <strong>Access</strong> your health records
                </li>
                <li>
                  <strong>Request corrections or amendments</strong> to your
                  records
                </li>
                <li>
                  <strong>Request restrictions</strong> on certain uses or
                  disclosures of your information
                </li>
                <li>
                  <strong>Receive an accounting</strong> of disclosures of your
                  PHI
                </li>
                <li>
                  <strong>Withdraw consent</strong> for certain uses of your
                  information (subject to legal requirements)
                </li>
              </ul>
              <p className="text-gray-600 leading-relaxed">
                Requests may be made by contacting us at help@plutonic.co.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                10. Data Retention
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We retain your personal and health information only as long as
                necessary to provide services, comply with legal obligations,
                and maintain business records, including those required by state
                mental health laws.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                11. International Users
              </h2>
              <p className="text-gray-600 leading-relaxed">
                If you use our Services from outside the United States, your
                information may be transferred to and stored in the U.S., where
                data protection laws may differ from those in your country.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                12. Changes to This Policy
              </h2>
              <p className="text-gray-600 leading-relaxed">
                We may update this Privacy Policy at any time. The revised
                version will be posted on our website with the effective date.
                Continued use of our Services after changes are posted
                constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                13. Contact Us
              </h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have questions, concerns, or complaints about this
                Privacy Policy or our data practices, please contact us:
              </p>
              <div className="text-gray-600 ml-4">
                <p>
                  <strong>Plutonic, Inc.</strong>
                </p>
                <p>3295 River Exchange Drive, Suite 567</p>
                <p>Norcross, GA 30092</p>
                <p>help@plutonic.co</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-500 text-center">
                Last updated: August 11th, 2025
              </p>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPage;
